// Implements: F-UIX-023, F-MCK-015, F-UIX-014, F-UIX-007
/**
 * The mockup's chips in the Lienzo editor's topbar (UX-05): where it lives and its tags.
 *
 * Both read the one shared detail query (`GET /mockups/{id}`) and open the existing containers in a
 * popover, so moving a mockup and tagging it keep every rule they had (the server decides; refusals
 * are read out; writes are never retried). The scope chip names the scope from the person's seats,
 * the viewer's own personal scope as "Personal". The tag panel is offered to whoever may tag
 * (`your_role` owner or editor); a viewer sees the tags only.
 */

import { useRef, useState } from "react";
import type { ReactElement } from "react";

import { useSession } from "../../../shared/api/session";
import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { ChipPopover } from "../../../shared/ui/molecules/ChipPopover";
import { Popover } from "../../../shared/ui/molecules/Popover";
import { buildScopeOptions, scopeValue } from "../components/scopeOptions";
import { useMockupDetail } from "../hooks/useMockupDetail";
import { MockupDetailContainer } from "./MockupDetailContainer";
import styles from "./MockupChipsContainer.module.css";
import { ScopeChangeContainer } from "./ScopeChangeContainer";

const EDITOR_ROLES: ReadonlySet<string> = new Set(["owner", "editor"]);

export interface MockupChipProps {
  readonly mockupId: string;
}

export function MockupScopeChipContainer({ mockupId }: MockupChipProps): ReactElement | null {
  const { t } = useTranslation();
  const { state } = useSession();
  const mockup = useMockupDetail(mockupId);
  if (mockup.data === undefined || state.status !== "authenticated") return null;

  const { scope_kind: kind, scope_id: id } = mockup.data;
  const current = scopeValue(kind, id, state.view.user.id);
  const seat = buildScopeOptions(state.view.scopes).find((option) => option.value === current);
  const label =
    seat?.label ?? (kind !== "personal" ? id : current === "personal:" ? t("scope.personal") : t("scope.personalOther"));

  // Owner round 3: the chip says what it is ("Ámbito: …") and its popover what a scope means.
  return (
    <ChipPopover
      label={t("scope.chip.label", { name: label })}
      title={t("scope.label")}
      description={t("editor.scope.chip")}
      leading={<Icon name={kind === "personal" ? "user" : "users"} size="sm" />}
      width="sm"
    >
      <p className={styles.explain}>{t("scope.explain")}</p>
      <ScopeChangeContainer mockupId={mockupId} />
    </ChipPopover>
  );
}

export function MockupTagsChipContainer({ mockupId }: MockupChipProps): ReactElement | null {
  const { t } = useTranslation();
  const mockup = useMockupDetail(mockupId);
  const anchor = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  if (mockup.data === undefined) return null;
  const mayTag = EDITOR_ROLES.has(mockup.data.your_role);

  return (
    <span className={styles.tags}>
      {mockup.data.tags.map((tag) => (
        <span key={tag} className={styles.tag}>{`#${tag}`}</span>
      ))}
      {mayTag ? (
        <>
          <span className={styles.trigger}>
            <Button
              variant="ghost"
              iconOnly
              size="sm"
              label={t("mockup.detail.addTag")}
              hasPopup="dialog"
              expanded={open}
              buttonRef={anchor}
              onClick={() => {
                setOpen((value) => !value);
              }}
            >
              <Icon name="tag" size="sm" />
            </Button>
          </span>
          <Popover
            open={open}
            onClose={() => {
              setOpen(false);
            }}
            anchorRef={anchor}
            label={t("mockup.detail.tags")}
            width="sm"
          >
            <div className={styles.panel}>
              <MockupDetailContainer mockupId={mockupId} section="tags" />
            </div>
          </Popover>
        </>
      ) : null}
    </span>
  );
}
